import { Building2, Link2Off, MapPin } from 'lucide-react';
import { useBrandFilter } from '@/components/brand-filter/brand-filter-context';

/**
 * The surface a chip sits on. Chat panes have their own palette (`--chat-*`, mirroring
 * WhatsApp's) and the shell tokens read wrong there — see the theming rules in CLAUDE.md.
 */
export type ChipSurface = 'shell' | 'chat';

const BRAND_CHIP_CLASSES: Record<ChipSurface, string> = {
  // Deliberately neutral: with 10–50 brands a per-brand hue turns every list into confetti,
  // and the colored chips already in the UI carry meaning (status, location).
  shell: 'bg-muted border-border text-muted-foreground',
  chat: 'bg-chat-elevated border-chat-border text-chat-text-muted',
};

const CHIP_BASE =
  'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap max-w-[160px]';

/**
 * Identifies which brand a row/detail belongs to. Renders nothing outside aggregate
 * sessions, so callers can drop it in unconditionally.
 */
export function BrandChip({
  brandName,
  surface = 'shell',
  className = '',
}: {
  brandName?: string | null;
  surface?: ChipSurface;
  className?: string;
}) {
  const { isAggregate } = useBrandFilter();
  if (!isAggregate || !brandName) return null;
  return (
    <span className={`${CHIP_BASE} ${BRAND_CHIP_CLASSES[surface]} ${className}`} title={brandName}>
      <Building2 className="h-3 w-3 shrink-0" />
      <span className="truncate">{brandName}</span>
    </span>
  );
}

/**
 * Identifies the branch a row/detail belongs to. Unconditional — unlike `ScopeChips` it renders
 * whenever a name is given, for the pre-existing line-session case (the inbox row of a
 * multi-branch brand). New aggregate labelling should go through `ScopeChips` instead.
 */
export function LocationChip({ locationName, className = '' }: { locationName?: string | null; className?: string }) {
  if (!locationName) return null;
  return (
    <span
      className={`${CHIP_BASE} border-purple-200 dark:border-purple-800/60 bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 ${className}`}
      title={locationName}
    >
      <MapPin className="h-3 w-3 shrink-0" />
      <span className="truncate">{locationName}</span>
    </span>
  );
}

/**
 * The order has no branch yet: on a shared-menu brand the cart is built before the branch is
 * settled, and it is settled at fulfilment. Muted rather than tinted — it is a state, not a
 * problem, and the confirm blocker is what stops it from ever being placed that way.
 */
export function PendingBranchChip({ label, className = '' }: { label: string; className?: string }) {
  return (
    <span
      className={`${CHIP_BASE} border-dashed border-border bg-muted/60 text-muted-foreground ${className}`}
      title={label}
    >
      <MapPin className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </span>
  );
}

const UNLINKED_CHIP_CLASSES =
  'border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300';

/**
 * The chat arrived on a sucursal's own WhatsApp line whose sucursal is not linked to a location.
 * The agent stays silent on such a line (campaigns-only) until the merchant links it in the
 * dashboard — the operator reading the inbox has to see WHY nobody answered. Renders in every
 * session kind: the state is about the line, not about who is looking. Same tint on both surfaces:
 * amber reads as a warning on the shell and on the chat palette alike.
 */
export function UnlinkedLineChip({ label, className = '' }: { label: string; className?: string }) {
  return (
    <span className={`${CHIP_BASE} ${UNLINKED_CHIP_CLASSES} ${className}`} title={label}>
      <Link2Off className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * Brand + branch together — the labeling every aggregate row and detail header carries.
 *
 * Aggregate-only by default, both chips: the backend labels rows on every aggregate-aware
 * endpoint whatever the session kind, so rendering on `locationName` alone would silently add
 * branch chips to surfaces line sessions have never had them on. A surface that wants the
 * branch chip in a line session too (the orders tab of a multi-branch brand, whose "All
 * branches" view mixes sucursales) opts in with `lineLocation` — the caller, not this
 * component, knows whether the brand has more than one branch.
 */
export function ScopeChips({
  brandName,
  locationName,
  surface = 'shell',
  className = '',
  lineLocation = false,
  pendingLocationLabel,
}: {
  brandName?: string | null;
  locationName?: string | null;
  surface?: ChipSurface;
  className?: string;
  /** In a line session, render the branch chip alone. No effect in aggregate sessions. */
  lineLocation?: boolean;
  /**
   * Shown in place of the branch chip when there is no branch yet (a shared-menu order that has
   * not reached fulfilment). Absent = say nothing, which is what every surface did before.
   */
  pendingLocationLabel?: string | null;
}) {
  const { isAggregate } = useBrandFilter();
  const branchChip = locationName ? (
    <LocationChip locationName={locationName} />
  ) : pendingLocationLabel ? (
    <PendingBranchChip label={pendingLocationLabel} />
  ) : null;

  if (!isAggregate) {
    // Unchanged shape for the line-session case: the single chip carries the caller's className.
    if (!lineLocation) return null;
    if (locationName) return <LocationChip locationName={locationName} className={className} />;
    if (pendingLocationLabel) return <PendingBranchChip label={pendingLocationLabel} className={className} />;
    return null;
  }
  if (!brandName && !branchChip) return null;
  return (
    <span className={`inline-flex min-w-0 flex-wrap items-center gap-1 ${className}`}>
      <BrandChip brandName={brandName} surface={surface} />
      {branchChip}
    </span>
  );
}
